/**
 * driverProfileResource — the learned driver-profile list, authored against the
 * resource-kit v2 model/view contracts and consumed directly as a
 * `ResourceBundle` by `ResourcePage` (#1805 slice A1). The
 * three record-shaped cell projections moved to `./widgets.tsx` as named
 * widgets; `fields[].render` has no legacy carrier.
 *
 * `pagination: 'client'` is what makes the three `filterable: false`
 * declarations load-bearing rather than tidy: a client-paged list is a BULK
 * source, so the framework offers filtering by default and there is no backend
 * whitelist to cap it. Drop one and the operator does not lose a filter — they
 * gain one, over a computed column with no backend key behind it.
 */
import {
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
} from '@resource-kit/model';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import type { components } from '@/types/api.generated';
import type { ListResult } from '@resource-kit/controllers';
import { PERMISSIONS } from '@/permissions';
import { http } from '@lib/api';

export type DriverProfile = components['schemas']['DriverProfileListItem'];
type DriverProfileList = components['schemas']['DriverProfileList'];

/**
 * The endpoint returns the whole set in one body — no page/size at all — so the
 * page is synthesised as a single full one and the framework pages it
 * client-side. `limit` floors at 1 so the framework's `ceil(total / limit)`
 * stays finite on an empty set (it yields 0 pages there, as before).
 */
export async function listDriverProfiles(): Promise<ListResult<DriverProfile>> {
  const result = await http.get<DriverProfileList>('/api/driver-profiles');
  const total = result.items.length;
  return { items: result.items, total, page: 1, limit: Math.max(total, 1) };
}

const f = createFieldConstructors<DriverProfile>();

export const driverProfileModel = defineModel<DriverProfile>()({
  name: 'driverProfile',
  idField: 'driver_id',
  recName: 'driver_name',
  fields: {
    driver_name: f.text({ label: 'driverProfiles.field.driver' }),
    // Both zone columns are computed one level down (`effective.*`), so they
    // are `virtual`: there is no property on the row to accessor into, and no
    // backend key to filter on.
    effectiveZones: f.virtual.array({
      label: 'driverProfiles.field.zones',
      filterable: false,
      widget: 'driverEffectiveZones',
    }),
    effectiveDailyRange: f.virtual.json({
      label: 'driverProfiles.field.dailyRange',
      filterable: false,
      widget: 'driverEffectiveDailyRange',
    }),
    learned_from: f.json({
      label: 'driverProfiles.field.learnedFrom',
      filterable: false,
      widget: 'driverLearnedFrom',
    }),
  },
});

export const driverProfileListView = defineListView(driverProfileModel, {
  items: [
    { field: 'driver_name' },
    { field: 'effectiveZones' },
    { field: 'effectiveDailyRange' },
    { field: 'learned_from' },
  ],
  defaultSort: { field: 'driver_name', dir: 'asc' },
});

/** Read-only list: the drawer shows the same columns; there is no form. */
export const driverProfileFormView = defineFormView(driverProfileModel, {
  groupDisplay: 'tab',
  children: [],
});

export const driverProfileResource: ResourceBundle<
  DriverProfile,
  typeof driverProfileModel.fields
> = {
  model: driverProfileModel,
  list: driverProfileListView,
  form: driverProfileFormView,
  host: {
    api: { list: listDriverProfiles },
    label: 'driverProfiles.label',
    slug: 'driver-profiles',
    listTitle: 'driverProfiles.listTitle',
    listDescription: 'driverProfiles.listDescription',
    // No `update` here on purpose (#2293): this bundle declares no
    // `api.update`, and `ResourcePage` short-circuits on that before it ever
    // consults the permission. Editing goes through this page's own override
    // action, which carries its own DISPATCH_WRITE gate.
    permission: {
      list: PERMISSIONS.DISPATCH_READ,
      read: PERMISSIONS.DISPATCH_READ,
    },
    pagination: 'client',
    defaultLimit: 100,
    searchPlaceholder: 'driverProfiles.searchPlaceholder',
    detail: { surface: 'drawer' },
  },
};
